<script setup lang="ts">
import { useVModel } from '@vueuse/core';
import { connectRefinementList } from 'instantsearch.js/es/connectors';
import {
  RefinementListConnectorParams,
  RefinementListWidgetDescription,
} from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import { computed, onMounted, PropType, watchEffect } from 'vue';
// @ts-expect-error (function lacks types; this should be converted in the near future)
import { createSuitMixin } from 'vue-instantsearch/vue3/es';

import Checkbox from '@/components/base/form/Checkbox.vue';
import CollapsibleWidget from '@/components/search/widgets/CollapsibleWidget.vue';
import { useWidget } from '@/composables/instant-search/useWidget';

defineOptions({
  mixins: [createSuitMixin({ name: 'RefinementList' })],
});

const props = defineProps({
  attribute: { required: true, type: String },
  header: { required: true, type: String },
  limit: { required: false, type: Number, default: 10 },
  modelValue: { required: false, type: Boolean, default: true },
  operator: {
    required: false,
    type: String as PropType<RefinementListConnectorParams['operator']>,
    default: 'or',
    validator(value) {
      return value === 'and' || value === 'or';
    },
  },
  optionCount: { required: false, type: Number, default: 0 },
  sortBy: {
    required: false,
    type: [Array, Function] as PropType<RefinementListConnectorParams['sortBy']>,
    default: () => ['isRefined', 'count:desc', 'name:asc'],
  },
  transformItems: {
    required: false,
    type: Function as PropType<RefinementListConnectorParams['transformItems']>,
  },
});

const emit = defineEmits(['click', 'update:modelValue', 'update:optionCount']);

const open = useVModel(props, 'modelValue', emit);

const widgetParams = computed<RefinementListConnectorParams>(() => ({
  attribute: props.attribute,
  limit: props.limit,
  operator: props.operator,
  sortBy: props.sortBy,
  transformItems: props.transformItems,
}));

const { state } = useWidget<RefinementListWidgetDescription, RefinementListConnectorParams>(
  { connector: connectRefinementList },
  widgetParams,
);

const showMenuPanel = computed(() => (state.value?.items.length ?? 0) > 0);
const refine = (value: string) => {
  state.value?.refine(value);
  emit('click');
};

const optionCount = useVModel(props, 'optionCount', emit);
onMounted(() => {
  watchEffect(() => {
    optionCount.value = state.value?.items.length ?? 0;
  });
});
</script>

<template>
  <div v-if="state">
    <CollapsibleWidget
      v-model="open"
      :showPanel="showMenuPanel"
      :attribute="attribute"
      :header="header"
    >
      <div :class="suit()">
        <ul :class="suit('list')" data-test="menu-list">
          <li
            v-for="item in state.items"
            :key="item.value"
            :class="[suit('item'), item.isRefined && suit('item', 'selected')]"
          >
            <Checkbox
              :modelValue="item.isRefined"
              @update:modelValue="refine(item.value)"
              :class="suit('label')"
              data-test="refinement-list-checkbox"
              :value="item.value"
              @keydown.enter="refine(item.value)"
            >
              <span :class="suit('labelText')">{{ item.label }}</span>
              <span :class="suit('count')">{{ item.count }}</span>
            </Checkbox>
          </li>
        </ul>
      </div>
    </CollapsibleWidget>
  </div>
</template>
